// src/admin/pages/AdminOwners.jsx
import { useEffect, useState, useCallback } from "react";
import AdminLayout from "../layout/AdminLayout";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../config/api";
import { adminAuthHeader } from "../../utils/authHeader";
import Pagination from "../../components/Pagination";

const TABS = [
  { key: "trial",   label: "🆓 Free Trial Owners",    color: "#0891b2", bg: "#ecfeff" },
  { key: "active",  label: "⭐ Standard Plan Owners", color: "#16a34a", bg: "#dcfce7" },
  { key: "pending", label: "⏳ Payment Pending",       color: "#d97706", bg: "#fef3c7" },
  { key: "blocked", label: "🔴 Blocked Owners",        color: "#7c3aed", bg: "#f5f3ff" },
  { key: "deleted", label: "🗑️ Deleted Owners",        color: "#6b7280", bg: "#f4f7fe" },
];

const AdminOwners = () => {
  const navigate = useNavigate();
  const [owners,     setOwners]     = useState([]);
  const [stats,      setStats]      = useState({});
  const [loading,    setLoading]    = useState(true);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const LIMIT = 10;
  const [search,    setSearch]    = useState("");
  const [tab,       setTab]       = useState("trial");
  const [exporting, setExporting] = useState(false);

  // ── Fetch UPI amount from settings ──────────────────────────
  const [upiAmount, setUpiAmount] = useState(300);

  const [activateModal, setActivateModal] = useState(null);
  const [activateForm,  setActivateForm]  = useState({ plan: "standard", amount: 300, months: 12, note: "" });
  const [activating,    setActivating]    = useState(false);
  const [activateMsg,   setActivateMsg]   = useState("");
  const [rejectModal,  setRejectModal]  = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting,    setRejecting]    = useState(false);
  // Create owner modal
  const [createModal,   setCreateModal]   = useState(false);
  const [createForm,    setCreateForm]    = useState({ name: "", email: "", phone: "", password: "", companyName: "", city: "", district: "", state: "", gstin: "", activate: false, plan: "standard", amount: 300, months: 12 });
  const [creating,      setCreating]      = useState(false);
  const [createMsg,     setCreateMsg]     = useState("");

  // Soft delete modal (move to Deleted tab)
  const [deleteModal,   setDeleteModal]   = useState(null);
  const [deleteReason,  setDeleteReason]  = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError,   setDeleteError]   = useState("");

  // Permanent delete modal (from Deleted tab)
  const [permModal,     setPermModal]     = useState(null);
  const [adminPassword, setAdminPassword] = useState("");
  const [deleteReports, setDeleteReports] = useState(false);
  const [permLoading,   setPermLoading]   = useState(false);
  const [permError,     setPermError]     = useState("");
  const [showPwd,       setShowPwd]       = useState(false);

  // ── Load UPI amount once on mount ───────────────────────────
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/settings/upi`, { headers: adminAuthHeader() })
      .then(r => r.json())
      .then(d => { if (d.amount) setUpiAmount(d.amount); })
      .catch(() => {});
  }, []);

  const fetchOwners = useCallback(async (currentPage = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: LIMIT, status: tab, ...(search && { search }) });
      const res = await axios.get(`${API_BASE_URL}/api/subscription/admin/owners?${params}`, { headers: adminAuthHeader() });
      setOwners(res.data.owners || []);
      setStats(res.data.stats   || {});
      setTotalPages(Math.ceil((res.data.owners?.length || 0) / LIMIT) || 1);
      setTotal(res.data.owners?.length || 0);
      setPage(currentPage);
    } catch (err) { console.error("Failed to load owners", err); setOwners([]); }
    setLoading(false);
  }, [search, tab]);

  useEffect(() => { setPage(1); fetchOwners(1); }, [fetchOwners]);

  const handleActivate = async () => {
    setActivating(true); setActivateMsg("");
    try {
      const res = await axios.put(`${API_BASE_URL}/api/subscription/admin/activate/${activateModal._id}`, activateForm, { headers: adminAuthHeader() });
      setActivateMsg("✅ " + res.data.message);
      setTimeout(() => { setActivateModal(null); setActivateMsg(""); fetchOwners(page); }, 1500);
    } catch (err) { setActivateMsg("❌ " + (err.response?.data?.message || "Failed")); }
    setActivating(false);
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
      await axios.put(`${API_BASE_URL}/api/subscription/admin/reject/${rejectModal._id}`, { reason: rejectReason }, { headers: adminAuthHeader() });
      setRejectModal(null); setRejectReason(""); fetchOwners(page);
    } catch { alert("Rejection failed"); }
    setRejecting(false);
  };

  const toggleBlock = async (userId, isBlocked) => {
    try {
      const url = isBlocked ? `${API_BASE_URL}/api/admin/owners/${userId}/unblock` : `${API_BASE_URL}/api/admin/owners/${userId}/block`;
      await axios.put(url, {}, { headers: adminAuthHeader() });
      fetchOwners(page);
    } catch { console.error("Block/Unblock failed"); }
  };

  // Soft delete — move to Deleted tab
  const handleDelete = async () => {
    setDeleteLoading(true); setDeleteError("");
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/owners/${deleteModal.userId}`, {
        headers: { ...adminAuthHeader(), "Content-Type": "application/json" },
        data: { reason: deleteReason },
      });
      setDeleteModal(null); setDeleteReason(""); fetchOwners(page);
    } catch (err) { setDeleteError(err.response?.data?.message || "Delete failed."); }
    setDeleteLoading(false);
  };

  // Recall — restore from Deleted tab
  const handleRecall = async (ownerId) => {
    try {
      await axios.put(`${API_BASE_URL}/api/admin/owners/${ownerId}/recall`, {}, { headers: adminAuthHeader() });
      fetchOwners(page);
    } catch { alert("Recall failed"); }
  };

  // Permanent delete — from Deleted tab, requires password
  const handlePermanentDelete = async () => {
    if (!adminPassword.trim()) { setPermError("Please enter your admin password."); return; }
    setPermLoading(true); setPermError("");
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/owners/${permModal.userId}/permanent`, {
        headers: { ...adminAuthHeader(), "Content-Type": "application/json" },
        data: { adminPassword, deleteReports },
      });
      setPermModal(null); setAdminPassword(""); setDeleteReports(false); fetchOwners(page);
    } catch (err) { setPermError(err.response?.data?.message || "Permanent delete failed."); }
    setPermLoading(false);
  };

  // Create owner manually
  const handleCreate = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      setCreateMsg("❌ Name, email and password are required.");
      return;
    }
    setCreating(true); setCreateMsg("");
    try {
      const res = await axios.post(`${API_BASE_URL}/api/admin/owners`, createForm, { headers: adminAuthHeader() });
      setCreateMsg("✅ " + res.data.message);
      setTimeout(() => {
        setCreateModal(false);
        setCreateMsg("");
        setCreateForm({ name: "", email: "", phone: "", password: "", companyName: "", city: "", district: "", state: "", gstin: "", activate: false, plan: "standard", amount: 300, months: 12 });
        fetchOwners(1);
      }, 1800);
    } catch (err) { setCreateMsg("❌ " + (err.response?.data?.message || "Failed to create owner")); }
    setCreating(false);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/owners/export-csv`, { headers: adminAuthHeader() });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `owners-${tab}-${new Date().toISOString().slice(0,10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Export failed"); }
    setExporting(false);
  };

  const currentTab = TABS.find(t => t.key === tab);

  return (
    <AdminLayout>
      <div className="admin-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <p className="admin-welcome">Owners Management</p>
          <h2 className="admin-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {currentTab?.label}
            {total > 0 && <span style={{ fontSize: 14, fontWeight: 500, color: "#a3aed0" }}>({total})</span>}
          </h2>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => { setCreateModal(true); setCreateMsg(""); }}
            style={{ padding: "10px 18px", background: "#4318FF", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            ➕ Create Owner
          </button>
          <button onClick={handleExportCSV} disabled={exporting || total === 0}
            style={{ padding: "10px 18px", background: exporting ? "#a3aed0" : "#16a34a", color: "#fff", border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            📥 {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        {TABS.map(t => (
          <div key={t.key} onClick={() => { setTab(t.key); setSearch(""); }} style={{ background: t.bg, borderRadius: 10, padding: "10px 16px", minWidth: 90, textAlign: "center", cursor: "pointer", border: tab === t.key ? `2px solid ${t.color}` : "2px solid transparent" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: t.color }}>{stats[t.key] ?? 0}</div>
            <div style={{ fontSize: 11, color: t.color, fontWeight: 600 }}>
              {{ trial: "Free Trial", active: "Standard", pending: "Pending", blocked: "Blocked", deleted: "Deleted" }[t.key]}
            </div>
          </div>
        ))}
        {stats.expiringSoon > 0 && (
          <div style={{ background: "#fef3c7", borderRadius: 10, padding: "10px 16px", minWidth: 90, textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#d97706" }}>{stats.expiringSoon}</div>
            <div style={{ fontSize: 11, color: "#d97706", fontWeight: 600 }}>Expiring Soon</div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 0, marginBottom: 16, borderBottom: "2px solid #e0e5f2" }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setSearch(""); }}
            style={{ padding: "10px 20px", border: "none", cursor: "pointer", background: "none", fontSize: 13, fontWeight: 700,
              color: tab === t.key ? t.color : "#a3aed0",
              borderBottom: tab === t.key ? `3px solid ${t.color}` : "3px solid transparent",
              marginBottom: "-2px", transition: "all 0.2s" }}>
            {t.label}
            <span style={{ marginLeft: 6, fontSize: 11, background: tab === t.key ? t.bg : "#f4f7fe", color: tab === t.key ? t.color : "#a3aed0", padding: "2px 7px", borderRadius: 20, fontWeight: 700 }}>
              {stats[t.key] ?? 0}
            </span>
          </button>
        ))}
      </div>

      <div className="admin-card" style={{ marginBottom: 16, padding: "14px 20px" }}>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ position: "relative", flex: 1 }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#a3aed0" }}>🔍</span>
            <input type="text" placeholder="Search by name, email, phone…" value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: "100%", height: 42, padding: "0 14px 0 36px", borderRadius: 10, border: "1px solid #e0e5f2", fontSize: 13, outline: "none", background: "#f9faff", color: "#1b2559", boxSizing: "border-box" }} />
          </div>
          {search && (
            <button onClick={() => setSearch("")}
              style={{ padding: "0 16px", background: "#fff1f1", color: "#ef4444", border: "1px solid #fecaca", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              ✕ Clear
            </button>
          )}
        </div>
      </div>

      <div className="admin-card">
        {loading ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#a3aed0" }}>⏳ Loading...</div>
        ) : owners.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#a3aed0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>{currentTab?.label.split(" ")[0]}</div>
            <p style={{ fontWeight: 600, color: "#1b2559" }}>No {tab} owners found</p>
          </div>
        ) : (
          <>
            {owners.map(owner => (
              <div key={owner._id} className="admin-owner-item">
                <div className="owner-left">
                  <h4>{owner.companyName || "—"}</h4>
                  <p>Owner: {owner.name || "—"}</p>
                  <p style={{ fontSize: 13, color: "#6b7280" }}>📧 {owner.email} &nbsp;·&nbsp; 📞 {owner.phone || "—"}</p>
                  {owner.city && <span>📍 {owner.city}</span>}

                  {tab === "pending" && (
                    owner.subscription?.transactionId ? (
                      <div style={{ marginTop: 8, background: "#f0fdf4", borderRadius: 8, padding: "8px 12px", display: "inline-block" }}>
                        <span style={{ fontSize: 11, color: "#16a34a", fontWeight: 600 }}>TXN ID: </span>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#1b2559", letterSpacing: 1 }}>{owner.subscription.transactionId}</span>
                        {owner.subscription.paymentMethod && (
                          <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 6 }}>via {owner.subscription.paymentMethod}</span>
                        )}
                      </div>
                    ) : (
                      <div style={{ marginTop: 8, background: "#fee2e2", borderRadius: 8, padding: "6px 10px", display: "inline-block" }}>
                        <span style={{ fontSize: 11, color: "#dc2626", fontWeight: 600 }}>⚠️ No transaction ID — do not activate</span>
                      </div>
                    )
                  )}

                  {tab === "trial" && (
                    <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <div style={{ background: "#ecfeff", borderRadius: 8, padding: "5px 10px", display: "inline-block" }}>
                        <span style={{ fontSize: 11, color: "#0891b2", fontWeight: 600 }}>🆓 Free Trial — using search-locked dashboard</span>
                      </div>
                      {owner.registeredAt && (
                        <div style={{ background: "#f4f7fe", borderRadius: 8, padding: "5px 10px", display: "inline-block" }}>
                          <span style={{ fontSize: 11, color: "#6b7280" }}>Joined {new Date(owner.registeredAt).toLocaleDateString("en-IN")}</span>
                        </div>
                      )}
                    </div>
                  )}


                  {tab === "active" && (
                    <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <div style={{ background: "#dcfce7", borderRadius: 8, padding: "5px 10px" }}>
                        <span style={{ fontSize: 11, color: "#16a34a", fontWeight: 700 }}>
                          ⭐ {owner.subscription?.plan ? owner.subscription.plan.charAt(0).toUpperCase() + owner.subscription.plan.slice(1) : "Standard"} Plan
                        </span>
                      </div>
                      {owner.subscription?.expiryDate && (
                        <div style={{ background: owner.daysRemaining <= 30 ? "#fef3c7" : "#f4f7fe", borderRadius: 8, padding: "5px 10px" }}>
                          <span style={{ fontSize: 11, color: owner.daysRemaining <= 30 ? "#d97706" : "#6b7280", fontWeight: 600 }}>
                            📅 Expires {new Date(owner.subscription.expiryDate).toLocaleDateString("en-IN")}
                            {owner.daysRemaining !== null && ` · ${owner.daysRemaining} days left`}
                          </span>
                        </div>
                      )}
                    </div>
                  )}


                  {tab === "deleted" && (
                    <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <div style={{ background: "#fee2e2", borderRadius: 8, padding: "5px 10px" }}>
                        <span style={{ fontSize: 11, color: "#dc2626", fontWeight: 600 }}>
                          🗑️ Deleted {owner.deletedAt ? new Date(owner.deletedAt).toLocaleDateString("en-IN") : ""}
                        </span>
                      </div>
                      {owner.deletedReason && (
                        <div style={{ background: "#fef3c7", borderRadius: 8, padding: "5px 10px" }}>
                          <span style={{ fontSize: 11, color: "#92400e" }}>Reason: {owner.deletedReason}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="owner-right">
                  <div className="owner-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {tab === "pending" && (
                      <>
                        <button onClick={() => { setActivateModal(owner); setActivateMsg(""); setActivateForm({ plan: "standard", amount: upiAmount, months: 12, note: "" }); }}
                          style={{ padding: "7px 14px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 12 }}>
                          ✅ Activate
                        </button>
                        <button onClick={() => { setRejectModal(owner); setRejectReason(""); }}
                          style={{ padding: "7px 14px", background: "#fee2e2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 12 }}>
                          ❌ Reject
                        </button>
                      </>
                    )}
                    {tab === "trial" && (
                      <button onClick={() => { setActivateModal(owner); setActivateMsg(""); setActivateForm({ plan: "standard", amount: upiAmount, months: 12, note: "" }); }}
                        style={{ padding: "7px 14px", background: "#4318FF", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 12 }}>
                        ⚡ Manually Upgrade
                      </button>
                    )}
                    {tab === "active" && (
                      <button onClick={() => toggleBlock(owner._id, false)}
                        style={{ padding: "7px 14px", background: "#fee2e2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 12 }}>
                        Block
                      </button>
                    )}
                    {tab === "blocked" && (
                      <button onClick={() => toggleBlock(owner._id, true)}
                        style={{ padding: "7px 14px", background: "#dcfce7", color: "#16a34a", border: "1px solid #bbf7d0", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 12 }}>
                        Unblock
                      </button>
                    )}
                    {tab === "deleted" ? (
                      <>
                        <button onClick={() => handleRecall(owner._id)}
                          style={{ padding: "7px 14px", background: "#4318FF", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 12 }}>
                          ♻️ Recall
                        </button>
                        <button onClick={() => { setPermModal({ ...owner, userId: owner._id }); setAdminPassword(""); setPermError(""); setDeleteReports(false); }}
                          style={{ padding: "7px 12px", background: "#fee2e2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 12 }}>
                          💀 Delete Forever
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="view-btn" onClick={() => navigate(`/admin/owners/${owner._id}`)}>View →</button>
                        <button onClick={() => { setDeleteModal({ ...owner, userId: owner._id }); setDeleteReason(""); setDeleteError(""); }}
                          style={{ padding: "7px 12px", background: "#fee2e2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 12 }}>
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <Pagination currentPage={page} totalPages={totalPages} total={total} limit={LIMIT} onPageChange={(p) => { setPage(p); fetchOwners(p); }} />
          </>
        )}
      </div>

      {/* ACTIVATE MODAL */}
      {activateModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 460, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ margin: "0 0 16px", color: "#1b2559" }}>✅ Activate Owner</h3>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1b2559", marginBottom: 2 }}>{activateModal.name}</div>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>{activateModal.email}</div>

              {activateModal.subscription?.transactionId ? (
                <div style={{ background: "#f0fdf4", border: "1.5px solid #16a34a40", borderRadius: 12, padding: "14px 16px" }}>
                  <div style={{ fontSize: 11, color: "#16a34a", fontWeight: 700, marginBottom: 6 }}>✅ TRANSACTION ID SUBMITTED BY OWNER</div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: "#1b2559", letterSpacing: 2 }}>
                    {activateModal.subscription.transactionId}
                  </div>
                  {activateModal.subscription.paymentMethod && (
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
                      💳 Paid via: <b>{activateModal.subscription.paymentMethod}</b>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ background: "#fef3c7", border: "1.5px solid #fde68a", borderRadius: 10, padding: "10px 14px" }}>
                  <div style={{ fontSize: 12, color: "#92400e", fontWeight: 600 }}>⚠️ No transaction ID submitted yet by this owner</div>
                </div>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <label style={lbl}>Plan</label>
                <select style={inp} value={activateForm.plan} onChange={e => setActivateForm(f => ({ ...f, plan: e.target.value }))}>
                  <option value="trial">Trial</option>
                  <option value="standard">Standard</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Amount Received (₹)</label>
                <input type="number" style={inp} value={activateForm.amount} onChange={e => setActivateForm(f => ({ ...f, amount: parseInt(e.target.value) || 0 }))} />
              </div>
              <div>
                <label style={lbl}>Validity (months)</label>
                <select style={inp} value={activateForm.months} onChange={e => setActivateForm(f => ({ ...f, months: parseInt(e.target.value) }))}>
                  <option value={1}>1 month</option>
                  <option value={3}>3 months</option>
                  <option value={6}>6 months</option>
                  <option value={12}>12 months</option>
                  <option value={24}>24 months</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Admin Note</label>
                <input style={inp} placeholder="Optional note" value={activateForm.note} onChange={e => setActivateForm(f => ({ ...f, note: e.target.value }))} />
              </div>
            </div>

            <div style={{ background: "#f0fdf4", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#16a34a", fontWeight: 600 }}>
              📧 Welcome email will be sent automatically to {activateModal.email}
            </div>

            {activateMsg && (
              <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontWeight: 600, fontSize: 13,
                background: activateMsg.startsWith("✅") ? "#dcfce7" : "#fee2e2",
                color:      activateMsg.startsWith("✅") ? "#16a34a"  : "#dc2626" }}>
                {activateMsg}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setActivateModal(null)} style={{ padding: "10px 20px", borderRadius: 8, border: "1.5px solid #e0e5f2", background: "#f4f7fe", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button onClick={handleActivate} disabled={activating}
                style={{ padding: "10px 22px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, color: "#fff", background: "#16a34a", opacity: activating ? 0.7 : 1 }}>
                {activating ? "Activating..." : "✅ Activate & Send Email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REJECT MODAL */}
      {rejectModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 420, maxWidth: "95vw" }}>
            <h3 style={{ margin: "0 0 6px", color: "#dc2626" }}>❌ Reject Owner</h3>
            <p style={{ color: "#6b7280", fontSize: 13, marginBottom: 16 }}><b>{rejectModal.name}</b> — {rejectModal.email}</p>
            <label style={lbl}>Reason for rejection</label>
            <input style={{ ...inp, marginBottom: 16 }} placeholder="e.g. Invalid transaction ID, payment not received…"
              value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setRejectModal(null)} style={{ padding: "10px 20px", borderRadius: 8, border: "1.5px solid #e0e5f2", background: "#f4f7fe", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button onClick={handleReject} disabled={rejecting}
                style={{ padding: "10px 22px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, color: "#fff", background: "#dc2626" }}>
                {rejecting ? "Rejecting..." : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CREATE OWNER MODAL */}
      {createModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 520, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ margin: "0 0 4px", color: "#1b2559" }}>➕ Create Owner Account</h3>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "#6b7280" }}>Manually add an owner without registration.</p>

            <p style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "#4318FF" }}>— Account Details</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={lbl}>Full Name *</label>
                <input style={inp} placeholder="e.g. Ravi Kumar" value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Email *</label>
                <input style={inp} type="email" placeholder="owner@email.com" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Phone</label>
                <input style={inp} placeholder="10-digit phone" value={createForm.phone} onChange={e => setCreateForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Password *</label>
                <input style={inp} type="text" placeholder="Set a password for them" value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} />
              </div>
            </div>

            <p style={{ margin: "12px 0 10px", fontSize: 12, fontWeight: 700, color: "#4318FF" }}>— Business Details</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={lbl}>Company Name</label>
                <input style={inp} placeholder="e.g. Vertex5 Car Rentals" value={createForm.companyName} onChange={e => setCreateForm(f => ({ ...f, companyName: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>City</label>
                <input style={inp} placeholder="City" value={createForm.city} onChange={e => setCreateForm(f => ({ ...f, city: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>District</label>
                <input style={inp} placeholder="District" value={createForm.district} onChange={e => setCreateForm(f => ({ ...f, district: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>State</label>
                <input style={inp} placeholder="State" value={createForm.state} onChange={e => setCreateForm(f => ({ ...f, state: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>GSTIN (optional)</label>
                <input style={inp} placeholder="GST number" value={createForm.gstin} onChange={e => setCreateForm(f => ({ ...f, gstin: e.target.value }))} />
              </div>
            </div>

            <p style={{ margin: "12px 0 10px", fontSize: 12, fontWeight: 700, color: "#4318FF" }}>— Subscription</p>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 14, padding: "12px 14px", background: createForm.activate ? "#f0fdf4" : "#f9faff", borderRadius: 10, border: `1.5px solid ${createForm.activate ? "#16a34a40" : "#e0e5f2"}` }}>
              <input type="checkbox" checked={createForm.activate} onChange={e => setCreateForm(f => ({ ...f, activate: e.target.checked }))} style={{ width: 16, height: 16 }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: createForm.activate ? "#16a34a" : "#1b2559" }}>
                ⚡ Activate immediately (Standard Plan)
              </span>
              <span style={{ fontSize: 12, color: "#6b7280" }}>— or leave unchecked for Free Trial</span>
            </label>

            {createForm.activate && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={lbl}>Plan</label>
                  <select style={inp} value={createForm.plan} onChange={e => setCreateForm(f => ({ ...f, plan: e.target.value }))}>
                    <option value="standard">Standard</option>
                    <option value="trial">Trial</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>Amount (₹)</label>
                  <input type="number" style={inp} value={createForm.amount} onChange={e => setCreateForm(f => ({ ...f, amount: parseInt(e.target.value) || 0 }))} />
                </div>
                <div>
                  <label style={lbl}>Validity</label>
                  <select style={inp} value={createForm.months} onChange={e => setCreateForm(f => ({ ...f, months: parseInt(e.target.value) }))}>
                    <option value={1}>1 month</option>
                    <option value={3}>3 months</option>
                    <option value={6}>6 months</option>
                    <option value={12}>12 months</option>
                    <option value={24}>24 months</option>
                  </select>
                </div>
              </div>
            )}

            {createMsg && (
              <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontWeight: 600, fontSize: 13,
                background: createMsg.startsWith("✅") ? "#dcfce7" : "#fee2e2",
                color:      createMsg.startsWith("✅") ? "#16a34a"  : "#dc2626" }}>
                {createMsg}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => { setCreateModal(false); setCreateMsg(""); }} style={{ padding: "10px 20px", borderRadius: 8, border: "1.5px solid #e0e5f2", background: "#f4f7fe", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button onClick={handleCreate} disabled={creating}
                style={{ padding: "10px 24px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, color: "#fff", background: "#4318FF", opacity: creating ? 0.7 : 1 }}>
                {creating ? "Creating…" : "➕ Create Owner"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SOFT DELETE MODAL — moves owner to Deleted tab */}
      {deleteModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 420, maxWidth: "95vw" }}>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 44, marginBottom: 8 }}>🗑️</div>
              <h3 style={{ margin: 0, color: "#1b2559", fontSize: 17 }}>Move to Deleted Tab</h3>
              <p style={{ color: "#6b7280", fontSize: 13, marginTop: 6 }}>
                <b>{deleteModal.name}</b> will be moved to the Deleted tab.<br/>
                Their data is safe — you can recall them anytime.
              </p>
            </div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#1b2559", marginBottom: 5 }}>Reason (optional)</label>
            <input placeholder="e.g. Duplicate account, inactive, requested deletion…" value={deleteReason}
              onChange={e => setDeleteReason(e.target.value)}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e0e5f2", fontSize: 13, outline: "none", boxSizing: "border-box", marginBottom: 14 }} />
            {deleteError && <p style={{ color: "#dc2626", fontSize: 12, marginBottom: 10, fontWeight: 600 }}>❌ {deleteError}</p>}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteModal(null)} style={{ padding: "10px 20px", borderRadius: 8, border: "1.5px solid #e0e5f2", background: "#f4f7fe", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button onClick={handleDelete} disabled={deleteLoading}
                style={{ padding: "10px 22px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, color: "#fff", background: "#6b7280", opacity: deleteLoading ? 0.7 : 1 }}>
                {deleteLoading ? "Moving…" : "🗑️ Move to Deleted"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PERMANENT DELETE MODAL — only from Deleted tab, requires password */}
      {permModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 440, maxWidth: "95vw" }}>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 44, marginBottom: 8 }}>💀</div>
              <h3 style={{ margin: 0, color: "#dc2626", fontSize: 17 }}>Permanently Delete</h3>
              <p style={{ color: "#6b7280", fontSize: 13, marginTop: 6 }}>This will <b>permanently remove</b> <b>{permModal.name}</b> from the database. This cannot be undone.</p>
            </div>
            <div style={{ background: "#f9faff", borderRadius: 10, padding: 14, marginBottom: 14 }}>
              <p style={{ margin: "0 0 10px", fontWeight: 700, fontSize: 13, color: "#1b2559" }}>What to do with their fraud reports?</p>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 8 }}>
                <input type="radio" name="permRep" checked={!deleteReports} onChange={() => setDeleteReports(false)} />
                <span style={{ fontSize: 13 }}><b>Keep reports</b> — fraud data stays visible</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input type="radio" name="permRep" checked={deleteReports} onChange={() => setDeleteReports(true)} />
                <span style={{ fontSize: 13, color: "#dc2626" }}><b>Delete all reports</b> — removes all fraud records</span>
              </label>
            </div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#1b2559", marginBottom: 5 }}>🔐 Admin password to confirm</label>
            <div style={{ position: "relative", marginBottom: 14 }}>
              <input type={showPwd ? "text" : "password"} placeholder="Your admin password" value={adminPassword} autoFocus
                onChange={e => { setAdminPassword(e.target.value); setPermError(""); }}
                onKeyDown={e => e.key === "Enter" && handlePermanentDelete()}
                style={{ width: "100%", padding: "10px 44px 10px 12px", borderRadius: 8, border: "1.5px solid #e0e5f2", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
              <button onClick={() => setShowPwd(!showPwd)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: 16 }}>
                {showPwd ? "🙈" : "👁️"}
              </button>
            </div>
            {permError && <p style={{ color: "#dc2626", fontSize: 12, marginBottom: 10, fontWeight: 600 }}>❌ {permError}</p>}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setPermModal(null)} style={{ padding: "10px 20px", borderRadius: 8, border: "1.5px solid #e0e5f2", background: "#f4f7fe", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button onClick={handlePermanentDelete} disabled={permLoading || !adminPassword.trim()}
                style={{ padding: "10px 22px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, color: "#fff",
                  background: !adminPassword.trim() ? "#a3aed0" : "#dc2626", opacity: permLoading ? 0.7 : 1 }}>
                {permLoading ? "Deleting…" : "💀 Delete Forever"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const lbl = { display: "block", fontSize: 12, fontWeight: 700, color: "#1b2559", marginBottom: 5 };
const inp = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e0e5f2", marginBottom: 0, fontSize: 13, outline: "none", boxSizing: "border-box" };

export default AdminOwners;